import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Detect iOS
    const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsIos(isIosDevice);

    if (isIosDevice && isSafari) {
      // Show iOS install guide if not dismissed before
      const dismissed = localStorage.getItem("pwa_ios_dismissed");
      if (!dismissed) setShowBanner(true);
      return;
    }

    // Listen for Chrome/Android install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const dismissed = localStorage.getItem("pwa_dismissed");
      if (!dismissed) setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem(isIos ? "pwa_ios_dismissed" : "pwa_dismissed", "1");
  };

  if (!showBanner) return null;

  if (isIos) {
    return (
      <div data-testid="pwa-ios-banner" className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-50 to-pink-50 border-t-2 border-amber-300 p-4 shadow-lg">
        <button onClick={handleDismiss} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" data-testid="pwa-dismiss-btn">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-start gap-3 max-w-lg mx-auto">
          <Download className="w-8 h-8 text-amber-600 shrink-0 mt-1" />
          <div>
            <p className="font-semibold text-gray-800 text-sm">Install Angel Massage App</p>
            <p className="text-xs text-gray-600 mt-1">
              Tap the <strong>Share</strong> button <span className="inline-block text-blue-500">&#xFEFF;↑</span> at the bottom of Safari, then tap <strong>"Add to Home Screen"</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-50 to-pink-50 border-t-2 border-amber-300 p-4 shadow-lg">
      <div className="flex items-center justify-between max-w-lg mx-auto gap-3">
        <div className="flex items-center gap-3">
          <Download className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="font-semibold text-gray-800 text-sm">Install Angel Massage</p>
            <p className="text-xs text-gray-500">Add to your home screen for quick access</p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="ghost" onClick={handleDismiss} className="text-xs" data-testid="pwa-dismiss-btn">
            Later
          </Button>
          <Button size="sm" onClick={handleInstall} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="pwa-install-btn">
            Install
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
